import React, {useCallback, useEffect, useState} from 'react';
import {Platform, View} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import {debounce} from 'lodash';

import {
  fetchVolumePerformanceUserList,
  resetReport,
  setFilters,
  setSort,
} from '/redux/action/volumePerformanceReport.action';
import {
  getResultsCount,
  getVolumePerformanceFilters,
  getVolumePerformanceSort,
  isListLoading,
} from '/redux/selector/volumePerformanceReport.selector';

import {
  DEFAULT_VOLUME_PERFORMANCE_FILTERS,
  FILTER_VALUES,
  ORDER_CATEGORIES,
  PERIODS,
  SORT_OPTIONS,
  TAB_NAMES,
} from '../constants/volumePerformanceReport.constants';
import {styles} from '../styles/VolumePerformanceContent.style';
import {
  MonthOption,
  VolumePerformanceContentProps,
} from '../types/VolPerfComponent.types';
import {VolumePerformanceFilters} from '../types/volumePerformanceReport.types';

import {VolPerfModalFilter} from './filter-modal';
import VolPerfFilters from './VolPerfFilters';
import VolPerfResultsSortSection from './VolPerfResultsSortSection';
import VolPerfSearchHeader from './VolPerfSearchHeader';
import VolPerfSortPopup from './VolPerfSortPopup';
import VolPerfTabs from './VolPerfTabs';
import VolumePerformanceDataRenderer from './VolumePerformanceDataRenderer';

export const VolumePerformanceContent = (
  props: VolumePerformanceContentProps,
) => {
  const {VolPerfUserId} = props;
  const dispatch = useDispatch<any>();
  const resultsCount = useSelector(getResultsCount) as number;
  const filters = useSelector(
    getVolumePerformanceFilters,
  ) as VolumePerformanceFilters;
  const sort = useSelector(getVolumePerformanceSort) as {
    sortBy: 'orderDate' | 'amount';
    sortOrder: 'ASC' | 'DESC';
  };
  const isLoading = useSelector(isListLoading) as boolean;

  const frontlineOnly = !!filters?.isFrontline;
  const activeTab =
    filters?.orderCategory === ORDER_CATEGORIES.TEMPLATES
      ? TAB_NAMES.SCHEDULED
      : TAB_NAMES.ORDERS;

  const filterCount = (() => {
    let count = 0;
    if (filters?.orderType && filters?.orderType !== FILTER_VALUES.ALL) {
      count++;
    }
    if (
      Array.isArray(filters?.orderStatus) &&
      filters.orderStatus.length > 0 &&
      filters.orderStatus[0] !== FILTER_VALUES.ALL
    ) {
      count++;
    } else if (
      typeof filters?.orderStatus === 'string' &&
      filters.orderStatus !== FILTER_VALUES.ALL
    ) {
      count++;
    }
    if (filters?.accountType && filters?.accountType !== FILTER_VALUES.ALL) {
      count++;
    }
    if (filters?.isNewClient === true) {
      count++;
    }
    if (filters?.consSubtotal !== undefined) {
      count++;
    }
    if (filters?.qv !== undefined) {
      count++;
    }
    return count;
  })();

  const [modalVisible, setModalVisible] = useState(false);
  const [sortModalVisible, setSortModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showStickySort, setShowStickySort] = useState(false);

  useEffect(() => {
    // Reset report state only when the component is completely unmounted (navigating back)
    return () => {
      dispatch(resetReport());
    };
  }, [dispatch]);

  // Debounced fetch function
  const debouncedFetch = useCallback(
    debounce((query: string) => {
      dispatch(fetchVolumePerformanceUserList(1, VolPerfUserId, query));
    }, 1500),
    [dispatch, VolPerfUserId],
  );

  useEffect(() => {
    // Fetch data whenever filters or sort options change
    // Using current searchValue for filter/sort changes
    dispatch(fetchVolumePerformanceUserList(1, VolPerfUserId, searchValue));
  }, [dispatch, VolPerfUserId, filters, sort]);

  const handleSearchChange = (text: string) => {
    setSearchValue(text);
    debouncedFetch(text);
  };

  const handleOpenModal = () => {
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleFrontlineToggle = () => {
    dispatch(setFilters({...filters, isFrontline: !frontlineOnly}));
  };

  const handleClearFilters = () => {
    dispatch(
      setFilters({
        ...filters,
        orderType: DEFAULT_VOLUME_PERFORMANCE_FILTERS.orderType,
        orderStatus: DEFAULT_VOLUME_PERFORMANCE_FILTERS.orderStatus,
        accountType: DEFAULT_VOLUME_PERFORMANCE_FILTERS.accountType,
        isNewClient: DEFAULT_VOLUME_PERFORMANCE_FILTERS.isNewClient,
        consSubtotal: DEFAULT_VOLUME_PERFORMANCE_FILTERS.consSubtotal,
        qv: DEFAULT_VOLUME_PERFORMANCE_FILTERS.qv,
      }),
    );
  };

  const handleTabPress = (tab: 'orders' | 'scheduled') => {
    const newCategory =
      tab === 'orders' ? ORDER_CATEGORIES.ORDERS : ORDER_CATEGORIES.TEMPLATES;
    if (filters?.orderCategory !== newCategory) {
      // Reset filters and frontline but preserve current month selection
      const newFilters: VolumePerformanceFilters = {
        ...DEFAULT_VOLUME_PERFORMANCE_FILTERS,
        orderCategory: newCategory,
        period: filters?.period || PERIODS.CURRENT_MONTH,
      };

      // Keep existing month logic: if switching to orders and next month is selected, reset to current month
      if (tab === 'orders' && filters?.period === PERIODS.NEXT_MONTH) {
        newFilters.period = PERIODS.CURRENT_MONTH;
      }

      dispatch(setFilters(newFilters));

      // Reset Sort to default (Date, newest to oldest)
      dispatch(setSort('orderDate', 'DESC'));

      // Reset Search Text
      setSearchValue('');
    }
  };

  const handleMonthSelect = (month: MonthOption) => {
    const period = month.key as '0' | '1' | '2';
    if (filters?.period !== period) {
      // Clear search text
      setSearchValue('');

      // Reset filters but preserve new period and current category
      const newFilters: VolumePerformanceFilters = {
        ...DEFAULT_VOLUME_PERFORMANCE_FILTERS,
        period,
        orderCategory: filters?.orderCategory || ORDER_CATEGORIES.ORDERS,
      };

      dispatch(setFilters(newFilters));
    }
  };

  const handleSortPress = () => {
    setSortModalVisible(true);
  };

  const handleApplyFilters = (
    newFilters: Partial<VolumePerformanceFilters>,
  ) => {
    dispatch(setFilters({...filters, ...newFilters}));
    setModalVisible(false);
  };

  const handleApplySort = (optionId: string) => {
    const selectedOption = SORT_OPTIONS.find(opt => opt.id === optionId);
    if (selectedOption) {
      dispatch(setSort(selectedOption.sortBy, selectedOption.sortOrder));
    }
    setSortModalVisible(false);
  };

  const handleScroll = useCallback((event: any) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    if (offsetY > 185) {
      setShowStickySort(true);
    } else {
      setShowStickySort(false);
    }
  }, []);

  const currentSortId = `${sort.sortBy}-${sort.sortOrder}`;
  const selectedSortLabel =
    SORT_OPTIONS.find(opt => opt.id === currentSortId)?.label ||
    'Date, newest to oldest';

  /**
   * Header section of the list containing all the filters and tabs.
   */
  const renderHeader = () => (
    <View pointerEvents={isLoading ? 'none' : 'auto'}>
      <VolPerfSearchHeader
        onMonthSelect={handleMonthSelect}
        onSearchChange={handleSearchChange}
        searchValue={searchValue}
        selectedPeriod={filters?.period || PERIODS.CURRENT_MONTH}
        activeTab={activeTab}
      />

      <VolPerfTabs activeTab={activeTab} onTabPress={handleTabPress} />

      <VolPerfFilters
        frontlineOnly={frontlineOnly}
        onFrontlineToggle={handleFrontlineToggle}
        onOpenModal={handleOpenModal}
        onClear={handleClearFilters}
        filterCount={filterCount}
      />

      <VolPerfResultsSortSection
        resultsCount={resultsCount}
        onSortPress={handleSortPress}
        sortByLabel={`Sort : ${selectedSortLabel}`}
        selectedSortBy={currentSortId}
      />

      {isLoading && <View style={styles.loadingOverlay} />}
    </View>
  );

  const content = (
    <>
      {showStickySort && (
        <View
          style={isLoading ? {opacity: 0.5} : undefined}
          pointerEvents={isLoading ? 'none' : 'auto'}>
          <VolPerfResultsSortSection
            resultsCount={resultsCount}
            onSortPress={handleSortPress}
            sortByLabel={`Sort : ${selectedSortLabel}`}
          />
        </View>
      )}

      <VolumePerformanceDataRenderer
        ListHeaderComponent={renderHeader()}
        VolPerfUserId={VolPerfUserId}
        searchValue={searchValue}
        onScroll={handleScroll}
      />
    </>
  );

  return (
    <View style={styles.root}>
      {Platform.OS === 'web' ? (
        <View style={styles.webScrollContainer as any}>{content}</View>
      ) : (
        content
      )}

      <VolPerfModalFilter
        isVisible={modalVisible}
        onClose={handleCloseModal}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      <VolPerfSortPopup
        isVisible={sortModalVisible}
        onClose={() => setSortModalVisible(false)}
        onSelect={handleApplySort}
        selectedOptionId={currentSortId}
        options={SORT_OPTIONS}
      />
    </View>
  );
};

export default VolumePerformanceContent;
